import { customElement, property } from 'lit/decorators.js';
import { style } from './link.styles';
import './link.scss';
import { html, LitElement, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import { SbkModal } from "@/components/modal/modal";

// eslint-disable-next-line
declare let _paq: any;

@customElement('sbk-link')
export class SbkLink extends LitElement {
    @property({ type: Boolean, attribute: 'automatic-download' }) automaticDownload = false;
    @property({ type: Boolean }) disabled = false;
    @property() icon = '';
    initialIcon = '';
    @property({ attribute: 'icon-position' }) iconPosition: 'left' | 'right' | '' = 'left';
    @property({ type: Boolean, attribute: 'is-bold' }) isBold = false;
    @property() href = '';
    @property({ attribute: 'data-parent' }) dataParent = '';
    @property() size: '2xs' | 'xs' | 'sm' | 'md' | '' = '';
    @property() target: '_blank' | '_parent' | '_self' | '_top' | '' = '';
    @property() variant: 'icon-navigation' | 'reduced-navigation' | 'navigation' | 'regular' | 'underlined' = 'underlined';
    @property({ type: Boolean, attribute: 'is-selected' }) isSelected = false;
    @property() hyphens: 'auto' | 'none' = 'auto';
    @property({ attribute: 'modal-trigger-id' }) modalTriggerId = '';
    @property({ type: Boolean, attribute: 'data-nosnippet' }) dataNoSnippet = false;
    @property({ type: Boolean, attribute: 'break-all' }) breakAll = false;

    static get styles() {
        return [style];
    }

    connectedCallback() {
        super.connectedCallback();
        this.initialIcon = this.icon;
        this.addEventListener('click', this.trackMatomo);
        if (this.href && this.automaticDownload) {
            setTimeout(() => (window.location.href = this.href), 1000);
        }
        if (this.modalTriggerId) {
            this.addEventListener('click', this._showModal);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.trackMatomo);
        this.removeEventListener('click', this._showModal);
    }

    private _showModal = (e: Event) => {
        e.preventDefault();
        const modal = document.querySelector(`#${this.modalTriggerId}`) as SbkModal;
        modal?.showModal();
    };

    render() {
        return html`
            <span class=${this.variant === 'navigation' ? 'link-wrapper__navigation' : `link-wrapper ${this.iconPosition} link-wrapper-${this.variant} link-${this.size} ${this.dataParent === 'infobox' ? 'link--parent-infobox' : ''}`}>
                ${this.disabled ? this.renderDisabledAsText() : this.href ? this.renderLink() : this.renderButton()}
            </span>
        `;
    }

    private renderLink() {
        let classes = [
            'link',
            [`link-${this.variant}`, this.isBold && 'bold', this.size].filter(Boolean).join('-'),
            this.isSelected ? 'link--selected' : '',
            this.hyphens === 'none' ? 'link--noHyphens' : '',
            this.icon ? `link--icon-position-${this.iconPosition}` : '',
            this.breakAll ? 'break-all' : ''
        ].filter(Boolean).join(' ');

        const isInBodyTextTag = ['p', 'span'].includes(this.parentElement?.tagName.toLowerCase() || '');
        const isInsideContactDataWrapper = this.closest('.table__card-content .contact-data__wrapper') !== null;

        if (this.variant === 'underlined' && isInBodyTextTag && !isInsideContactDataWrapper) {
            classes += ' link--inBodytext';
        }

        return html`
            <a part="link" class=${classes}
               ?data-nosnippet=${this.dataNoSnippet}
               href=${this.href}
               title=${this.title || nothing}
               target=${this.target || nothing}
            >
                ${when(this.icon && this.iconPosition === "left", this.renderIcon)}
                <span class="link__text"><slot></slot></span>
                ${when(this.icon && this.iconPosition === "right", this.renderIcon)}
            </a>
        `;
    }

    private renderButton() {
        const classes = [
            'link',
            [`link-${this.variant}`, this.isBold && 'bold', this.size].filter(Boolean).join('-'),
            this.isSelected ? 'link--selected' : '',
            this.hyphens === 'none' ? 'link--noHyphens' : '',
            this.icon ? `link--icon-position-${this.iconPosition}` : ''
        ].filter(Boolean).join(' ');

        return html`
            <button class=${classes}>
                ${when(this.icon && this.iconPosition === "left", this.renderIcon)}
                <span class="link__text"><slot></slot></span>
                ${when(this.icon && this.iconPosition === "right", this.renderIcon)}
            </button>
        `;
    }

    private renderDisabledAsText() {
        const classes = [
            'link ',
            'link--disabled',
            `link-${this.variant}`,
            this.isBold ? 'bold' : '',
            this.size ? `${this.size}` : '',
            this.hyphens === 'none' ? 'link--noHyphens' : ''
        ].filter(Boolean).join(' ');

        return html`
            <p class=${classes}>
                ${when(this.icon && this.iconPosition === "left", this.renderIcon)}
                <slot></slot>
                ${when(this.icon && this.iconPosition === "right", this.renderIcon)}
            </p>
        `;
    }

    private renderIcon = () => html`
        <span class="link__icon">
            <sbk-icon size=${this.size === 'xs' || this.size === '2xs' ? 20 : 24} symbol=${this.icon}></sbk-icon>
        </span>
    `;

    private trackMatomo = () => {
        if (this.href?.endsWith('@sbk.org')) {
            _paq.push(['trackGoal', 12]);
        }
    };
}
